// Founder-directed customer-facing sheet-assignment workflow
// (2026-09-09) -- real UI on top of the existing, already-tested
// backend endpoints (routers/invitations.py):
//   GET/PUT /projects/{project_id}/members/{user_id}/sheet-assignments
// Restricted roles (subcontractor, foreman -- services/sheet_membership.py
// RESTRICTED_ROLES) see ONLY the sheets checked here: no assignment =
// zero sheets, enforced server-side across listing/detail/binary/
// export/offline-manifest/search/related-object read paths (FP-016,
// closed). This modal is the admin-facing half of that contract --
// authorized assignment/unassignment, reloaded from the server (not
// optimistic-only) so persistence is genuinely proven, not assumed.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, FileWarning } from "lucide-react";

export default function SheetAssignmentModal({ open, onClose, targetUser }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [sheets, setSheets] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProjectId("");
    setSheets([]);
    setAssignedIds(new Set());
    api.get("/projects").then(({ data }) => setProjects(data || [])).catch(() => setProjects([]));
  }, [open]);

  useEffect(() => {
    if (!open || !projectId || !targetUser) return;
    setLoadingSheets(true);
    Promise.all([
      api.get(`/projects/${projectId}/sheets`),
      api.get(`/projects/${projectId}/members/${targetUser.id}/sheet-assignments`),
    ])
      .then(([sheetsRes, assignRes]) => {
        setSheets(sheetsRes.data || []);
        setAssignedIds(new Set(assignRes.data?.sheet_ids || []));
      })
      .catch(() => toast.error("Could not load sheets for this project"))
      .finally(() => setLoadingSheets(false));
  }, [open, projectId, targetUser]);

  const toggle = (sheetId) => {
    setAssignedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sheetId)) next.delete(sheetId); else next.add(sheetId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/projects/${projectId}/members/${targetUser.id}/sheet-assignments`,
        { sheet_ids: Array.from(assignedIds) },
      );
      // Re-hydrate from the server's own response (not just the local
      // set we sent) so what's shown is what was actually persisted --
      // e.g. a stale sheet_id no longer in this project is silently
      // dropped server-side.
      setAssignedIds(new Set(data.sheet_ids || []));
      toast.success(`Sheet access updated — ${data.sheet_ids.length} sheet(s) assigned to ${targetUser.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sheet assignments");
    } finally {
      setSaving(false);
    }
  };

  if (!targetUser) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="sheet-assignment-modal">
        <DialogHeader>
          <DialogTitle>Sheet access — {targetUser.name}</DialogTitle>
        </DialogHeader>

        <div className="flex items-start gap-2 text-xs text-[var(--fop-text-3)] bg-[var(--fop-surface-2)] rounded-md p-2.5 mb-1" data-testid="sheet-assignment-offline-notice">
          <FileWarning size={14} className="mt-0.5 shrink-0" />
          <span>
            {targetUser.role === "foreman" ? "Foremen" : "Subcontractors"} only see, search, export, and can
            sync offline the sheets checked below. Everything else in this project — including its list,
            search results, and related-record links — stays completely hidden until assigned here.
          </span>
        </div>

        <Select value={projectId ?? undefined} onValueChange={setProjectId}>
          <SelectTrigger data-testid="sheet-assignment-project-select">
            <SelectValue placeholder="Choose a project…" />
          </SelectTrigger>
          <SelectContent>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`sheet-assignment-project-option-${p.id}`}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {projectId && (
          loadingSheets ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[var(--fop-text-3)]" /></div>
          ) : sheets.length === 0 ? (
            <div className="text-sm text-[var(--fop-text-3)] py-4 text-center" data-testid="sheet-assignment-empty">
              This project has no sheets yet.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-[var(--fop-text-3)] px-0.5">
                <span data-testid="sheet-assignment-count">{assignedIds.size} of {sheets.length} assigned</span>
                <div className="flex gap-3">
                  <button className="underline hover:text-[var(--fop-text)]" data-testid="sheet-assignment-select-all"
                          onClick={() => setAssignedIds(new Set(sheets.map((s) => s.id)))}>
                    Select all
                  </button>
                  <button className="underline hover:text-[var(--fop-text)]" data-testid="sheet-assignment-select-none"
                          onClick={() => setAssignedIds(new Set())}>
                    Select none
                  </button>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto border border-[var(--fop-border-2)] rounded-md divide-y divide-[var(--fop-border-2)]" data-testid="sheet-assignment-list">
                {sheets.map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--fop-surface-2)]"
                          data-testid={`sheet-assignment-row-${s.id}`}>
                    <Checkbox
                      checked={assignedIds.has(s.id)}
                      onCheckedChange={() => toggle(s.id)}
                      data-testid={`sheet-assignment-checkbox-${s.id}`}
                    />
                    <span className="font-mono text-[var(--fop-text-3)] w-20 shrink-0">{s.sheet_number}</span>
                    <span className="truncate text-[var(--fop-text)]">{s.sheet_title}</span>
                  </label>
                ))}
              </div>
            </>
          )
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sheet-assignment-cancel-btn">Close</Button>
          <Button
            onClick={save}
            disabled={!projectId || saving || loadingSheets}
            data-testid="sheet-assignment-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save assignments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
